import React, { useEffect } from 'react';
import { Row, Col, Typography, Button, InputNumber, Form } from 'antd';
import CustomSelect from "../../../components/CustomSelect/CustomSelect";

const { Text } = Typography;

const SupplierAllocation = ({ supplierOptions, form }) => {
  const watchedSuppliers = Form.useWatch('supplierIds', form);
  const selectedSuppliers = watchedSuppliers !== undefined ? watchedSuppliers : (form?.getFieldValue?.('supplierIds') || []);

  const watchedTotal = Form.useWatch('fundReq', form);
  const totalAmount = watchedTotal !== undefined ? watchedTotal : (form?.getFieldValue?.('fundReq') || 0);

  const watchedAllocations = Form.useWatch('allocations', form);
  const allocations = watchedAllocations !== undefined ? watchedAllocations : (form?.getFieldValue?.('allocations') || {});

  const handleSplitEvenly = () => {
    if (!selectedSuppliers.length) return;
    const total = Number(totalAmount) || 0;
    const count = selectedSuppliers.length;
    const splitAmt = Number((total / count).toFixed(2));
    
    selectedSuppliers.forEach((id, index) => {
      let amt = splitAmt;
      if (index === count - 1) {
        amt = Number((total - (splitAmt * (count - 1))).toFixed(2));
      }
      form.setFieldValue(['allocations', id, 'amount'], amt);
    });
  };

  const totalAllocated = selectedSuppliers.reduce((sum, id) => {
    const amt = allocations?.[id]?.amount || 0;
    return sum + Number(amt);
  }, 0);

  const remainingAmount = Number((totalAmount - totalAllocated).toFixed(2));
  const isExceeded = totalAmount > 0 && Number((totalAllocated - totalAmount).toFixed(2)) > 0;

  // Trigger re-validation of fundReq whenever totalAllocated changes
  useEffect(() => {
    if (form && typeof form.validateFields === 'function') {
      form.validateFields(['fundReq']).catch(() => {});
    }
  }, [totalAllocated, form]);

  return (
    <div style={{ width: '100%', marginTop: 16 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <CustomSelect
            label="Suppliers"
            isRequired={true}
            name="supplierIds"
            placeholder="Select Suppliers"
            options={supplierOptions}
            mode="multiple"
            showSearch
            rules={[
              {
                required: true,
                message: "Please select at least one supplier",
              },
            ]}
          />
          {supplierOptions.length === 0 && (
            <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
              No suppliers found for this business. Please add suppliers before submitting a purchase order.
            </Text>
          )}
        </Col>
      </Row>

      {selectedSuppliers.length > 0 && (
        <div style={{ marginTop: 16, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col><Text strong>Supplier Allocation Breakdown</Text></Col>
            <Col><Button type="link" onClick={handleSplitEvenly} style={{ padding: 0 }}>Split Evenly</Button></Col>
          </Row>
          
          <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
            <Col xs={24} md={12}><Text type="secondary">Supplier</Text></Col>
            <Col xs={24} md={12}><Text type="secondary">Amount (₹)</Text></Col>
          </Row>

          {selectedSuppliers.map(id => {
            const supplier = supplierOptions.find(s => s.value === id);
            const currentAmount = Number(allocations?.[id]?.amount || 0);

            const hasAmountError =
              totalAmount > 0 &&
              (currentAmount > totalAmount || (isExceeded && currentAmount > 0));

            const amountHelpText =
              currentAmount > totalAmount
                ? "Allocation cannot exceed funding amount"
                : isExceeded && currentAmount > 0
                ? "Total allocation cannot exceed funding amount"
                : undefined;

            return (
              <Row gutter={[16, 16]} key={id} align="top" style={{ marginBottom: 12 }}>
                <Col xs={24} md={12} style={{ paddingTop: 5 }}>
                  <Text>{supplier?.label || id}</Text>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={['allocations', id, 'amount']}
                    style={{ marginBottom: 0 }}
                    validateStatus={hasAmountError ? "error" : undefined}
                    help={amountHelpText}
                    rules={[
                      {
                        required: true,
                        message: "Amount is required",
                      },
                      {
                        validator(_, val) {
                          if (val !== undefined && val !== null && val !== "") {
                            const num = Number(val);
                            if (num <= 0) return Promise.reject(new Error("Amount must be greater than 0"));
                            if (totalAmount > 0 && num > totalAmount) {
                              return Promise.reject(new Error("Allocation cannot exceed funding amount"));
                            }
                            if (totalAmount > 0 && Number((totalAllocated - totalAmount).toFixed(2)) > 0) {
                              return Promise.reject(new Error("Total allocation cannot exceed funding amount"));
                            }
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <InputNumber
                      min={0}
                      prefix="₹"
                      placeholder="Amount"
                      status={hasAmountError ? "error" : undefined}
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            );
          })}
          
          <Row justify="end" style={{ marginTop: 16 }}>
            <Col>
              <Text type="secondary">Total Allocated: </Text>
              <Text strong>₹ {totalAllocated.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              <Text type="secondary" style={{ marginLeft: 16 }}>Remaining: </Text>
              <Text strong type={remainingAmount < 0 ? 'danger' : (remainingAmount === 0 ? 'success' : 'warning')}>
                ₹ {remainingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default SupplierAllocation;
